/**
 * ExcalidrawViewer — view-only React component for `.excalidraw` scenes.
 *
 * Wraps `@excalidraw/excalidraw` in `viewModeEnabled` so the user gets
 * Obsidian's pan/zoom/copy interactions but no edit toolbar. The library
 * is loaded via dynamic `import()` so:
 *
 *   1. The unit-test environment (vitest `node`) can import this module
 *      to test the pure helpers below without needing the library on disk.
 *   2. The bundler can code-split the (~1MB) Excalidraw payload off the
 *      main route — users who never open a drawing don't pay the download
 *      cost.
 *
 * The component is **stateless about the scene contents** — editing lands
 * post-v1 only if the round-trip guarantee in `program-type.md` can be
 * proven. Until then we explicitly hide every Excalidraw write affordance
 * (`UIOptions.canvasActions` flags below).
 *
 * Pure helpers at the bottom of the file (`pickViewerProps`,
 * `sceneHasGeometry`, `summariseScene`) are exported so unit tests can
 * verify the wiring without instantiating React.
 */

import type { ExcalidrawScene } from '@obsidianweb/vault-parsers';
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

/* ── Scene type ────────────────────────────────────────────────────────── *
 * The canonical `ExcalidrawScene` lives in `@obsidianweb/vault-parsers`
 * (the server parses the file and ships this shape over the wire).
 * Imported type-only — erased at compile time, so the parser package adds
 * nothing to the browser bundle — and re-exported for this component's
 * consumers/tests. */
export type { ExcalidrawScene } from '@obsidianweb/vault-parsers';

/* ── Public props ─────────────────────────────────────────────────────── */

export interface ExcalidrawViewerProps {
  /** Parsed scene (raw or wrapper-extracted). */
  readonly scene: ExcalidrawScene;
  /** Viewport size in CSS pixels. Defaults are sized for the content pane. */
  readonly width?: number;
  readonly height?: number;
  /**
   * Test seam — inject a fake Excalidraw component constructor. When unset
   * (production), the real library is loaded via dynamic import.
   */
  readonly loadExcalidrawModule?: () => Promise<ExcalidrawModule>;
}

/** Shape of the upstream module we depend on. Kept structural so we don't
 *  pull `@excalidraw/excalidraw` into the TS resolution graph — the only
 *  field we care about is the default-exported React component. */
export interface ExcalidrawModule {
  readonly Excalidraw: ComponentType<ExcalidrawComponentProps>;
}

/** Subset of the upstream component's props we depend on. Kept minimal so
 *  upstream version bumps that *add* props don't force a code change here. */
export interface ExcalidrawComponentProps {
  readonly initialData?: {
    readonly elements: readonly unknown[];
    readonly appState?: Readonly<Record<string, unknown>>;
    readonly files?: Readonly<Record<string, unknown>>;
    readonly scrollToContent?: boolean;
  };
  readonly viewModeEnabled?: boolean;
  readonly zenModeEnabled?: boolean;
  readonly gridModeEnabled?: boolean;
  readonly theme?: 'light' | 'dark';
  readonly UIOptions?: Readonly<{
    canvasActions?: Readonly<{
      changeViewBackgroundColor?: boolean;
      clearCanvas?: boolean;
      export?: boolean | Readonly<Record<string, unknown>>;
      loadScene?: boolean;
      saveToActiveFile?: boolean;
      saveAsImage?: boolean;
      theme?: boolean;
      toggleTheme?: boolean;
    }>;
    tools?: Readonly<{ image?: boolean }>;
  }>;
}

/* ── Pure helpers (exported for unit tests) ───────────────────────────── */

/**
 * Compose the props handed to `<Excalidraw />`. Centralised here so the
 * unit tests can assert that `viewModeEnabled` is on and that every write
 * action is disabled — Task 8's view-only contract.
 */
export function pickViewerProps(scene: ExcalidrawScene): ExcalidrawComponentProps {
  const theme = pickTheme(scene.appState);
  const initialData: ExcalidrawComponentProps['initialData'] = {
    elements: scene.elements,
    appState: scene.appState,
    files: scene.files,
    // `scrollToContent` pans/zooms so the bounding box fits — without it,
    // wrapper-extracted scenes (which often omit `scrollX`/`scrollY`)
    // render with the camera at the origin and the user sees an empty
    // canvas.
    scrollToContent: true,
  };
  return {
    initialData,
    viewModeEnabled: true,
    zenModeEnabled: false,
    gridModeEnabled: false,
    ...(theme ? { theme } : {}),
    UIOptions: {
      canvasActions: {
        changeViewBackgroundColor: false,
        clearCanvas: false,
        // Export is harmless — it's a read-side operation that doesn't
        // touch the vault file — so we leave it enabled.
        export: { saveFileToDisk: true },
        loadScene: false,
        saveToActiveFile: false,
        saveAsImage: true,
        theme: false,
        toggleTheme: false,
      },
      tools: { image: false },
    },
  };
}

function pickTheme(appState: Readonly<Record<string, unknown>>): 'light' | 'dark' | undefined {
  const t = appState['theme'];
  if (t === 'light' || t === 'dark') return t;
  return undefined;
}

/** Returns `true` iff the scene has at least one renderable, non-deleted
 *  element. The fallback path uses this to decide between "drawing"
 *  vs. "this drawing is empty" messaging. */
export function sceneHasGeometry(scene: ExcalidrawScene): boolean {
  for (const el of scene.elements) {
    if (typeof el !== 'object' || el === null) continue;
    const e = el as Record<string, unknown>;
    if (e['isDeleted'] === true) continue;
    return true;
  }
  return false;
}

/** Human-readable summary used by the status bar / empty state. */
export function summariseScene(scene: ExcalidrawScene): string {
  let live = 0;
  let deleted = 0;
  for (const el of scene.elements) {
    if (typeof el !== 'object' || el === null) continue;
    const e = el as Record<string, unknown>;
    if (e['isDeleted'] === true) deleted += 1;
    else live += 1;
  }
  const parts = [`${live} element${live === 1 ? '' : 's'}`];
  if (deleted > 0) parts.push(`${deleted} hidden`);
  parts.push(`v${scene.version}`);
  return parts.join(' · ');
}

/* ── Component ────────────────────────────────────────────────────────── */

type LoadState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly module: ExcalidrawModule }
  | { readonly status: 'error'; readonly message: string };

/**
 * Default loader — dynamic-imports the real Excalidraw package (a proper
 * dependency of this app; vite code-splits it into its own chunk so the
 * shell bundle stays small). The function seam lets tests inject a fake.
 */
const defaultLoader = (): Promise<ExcalidrawModule> => {
  return import('@excalidraw/excalidraw').then((m) => ({
    Excalidraw: (m.Excalidraw ?? (m as { default?: unknown }).default) as ComponentType<ExcalidrawComponentProps>,
  }));
};

export function ExcalidrawViewer(props: ExcalidrawViewerProps): JSX.Element {
  const width = props.width ?? 800;
  const height = props.height ?? 600;
  const [state, setState] = useState<LoadState>({ status: 'idle' });
  const loaderRef = props.loadExcalidrawModule ?? defaultLoader;

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    loaderRef()
      .then((mod) => {
        if (cancelled) return;
        setState({ status: 'ready', module: mod });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ status: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [loaderRef]);

  const viewerProps = useMemo(() => pickViewerProps(props.scene), [props.scene]);
  const summary = useMemo(() => summariseScene(props.scene), [props.scene]);

  let body: ReactNode;
  if (state.status === 'loading' || state.status === 'idle') {
    body = (
      <p style={{ color: 'var(--text-muted, #a1a1aa)', padding: '24px' }}>
        Loading Excalidraw renderer…
      </p>
    );
  } else if (state.status === 'error') {
    body = (
      <p style={{ color: 'var(--text-error, #e93147)', padding: '24px' }}>
        Failed to load Excalidraw renderer: {state.message}
      </p>
    );
  } else {
    const Excalidraw = state.module.Excalidraw;
    body = <Excalidraw {...viewerProps} />;
  }

  return (
    <div
      className="ow-excalidraw-viewer"
      data-testid="excalidraw-viewer-root"
      style={{
        width,
        height,
        position: 'relative',
        background: 'var(--background-primary, #ffffff)',
      }}
    >
      <div
        className="ow-excalidraw-canvas"
        data-testid="excalidraw-canvas-host"
        style={{ width: '100%', height: 'calc(100% - 24px)' }}
      >
        {body}
      </div>
      <div
        className="ow-excalidraw-status"
        data-testid="excalidraw-status"
        style={{
          fontSize: 11,
          color: 'var(--text-muted, #a1a1aa)',
          padding: '4px 8px',
          borderTop: '1px solid var(--background-modifier-border, #2a2a2a)',
          background: 'var(--background-secondary, #181818)',
        }}
      >
        Read-only · {summary}
      </div>
    </div>
  );
}

export default ExcalidrawViewer;
